import { PARAGRAPH_KEY, TEXT_KEY, SELECTION_KEY } from '@techchange/pm-specs';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

export const mentionTriggerPluginKey = new PluginKey('mentionTriggerPlugin');

const MENTION_REGEX = /(^|\s)@([\w-.]*)$/;

/**
 * Plugin that manages the state for triggering an at-mention.
 *
 * @returns {Object} Prosemirror Plugin object.
 */
const mentionTriggerPlugin = () =>

	new Plugin({

		key: mentionTriggerPluginKey,

		state: {

			// Initializes the plugin state
			init() {

				return {

					/**
				 	 * The start position of the mention text.
				 	 */
					mentionStart: null,

					/**
				 	 * The end position of the mention text.
				 	 */
					mentionEnd: null,

					/**
					 * The mention query text the user is typing.
					 */
					queryText: null,

					/**
					 * Flag specifying if the mention dropdown should be shown.
					 */
					isMentionActive: false,

				};

			},

			// Updates the plugin state given incoming editor transaction
			apply(tr, pluginState, prevState, nextState) {

				const { docChanged, selection } = tr;
				const { schema } = nextState;

				const newPluginState = Object.assign({}, pluginState);

				if (tr.getMeta('selectRef') !== undefined) {

					newPluginState.selectRef = tr.getMeta('selectRef');

				}

				// No need to parse for mentions if content is unchanged
				if (!docChanged) {

					return newPluginState;

				}

				// Check if we are deactivating the mention state
				const deactivated = tr.getMeta('deactivateMention');

				if (deactivated) {

					newPluginState.isMentionActive = false;
					newPluginState.mentionStart = null;
					newPluginState.mentionEnd = null;

					return newPluginState;

				}

				// Otherwise test the text node at the current selection
				// to see if it matches the at-mention pattern
				const { from, to, $from } = selection;

				const paragraphNodeType = schema.nodes[PARAGRAPH_KEY];
				const textNodeType = schema.nodes[TEXT_KEY];
				const selectionMarkType = schema.marks[SELECTION_KEY];

				let queryText, mentionStart, mentionEnd;

				if (from === to) {

					const parentPos = $from.before();
					const parentNode = $from.parent;

					if (parentNode && parentNode.type === paragraphNodeType) {

						parentNode.descendants((node, offset) => {

							const absStart = parentPos + 1 + offset;
							const absEnd = absStart + node.nodeSize;

							// Check if we've marked this text to not be matched
							const selectionMarks = node.marks.filter((mark) => mark.type === selectionMarkType);

							if (node.type === textNodeType && selectionMarks.length === 0) {

								// Check if the current selection is within the text node
								if (from >= absStart && from <= absEnd) {

									// Get the text between the beginning of the text node
									//	up to the current cursor selection
									const text = $from.doc.textBetween(absStart, from);

									const match = text.match(MENTION_REGEX);

									if (match && match.length === 3) {

										// Get the user's name text from the second capturing group
										queryText = match[2];

										// Get the absolute position of the '@' character,
										//	(which starts at the end of the first capturing group)
										mentionStart = absStart + match.index + match[1].length;

										// Get the absolute end position of the user's name text being typed
										mentionEnd = absStart + match.index + match[0].length;

									}

								}

							}

						});

					}

				}

				// Check if there's a match
				if (queryText !== undefined && mentionStart !== undefined && mentionEnd !== undefined) {

					newPluginState.isMentionActive = true;
					newPluginState.queryText = queryText;
					newPluginState.mentionStart = mentionStart;
					newPluginState.mentionEnd = mentionEnd;

				// Otherwise deactivate
				} else {

					newPluginState.isMentionActive = false;
					newPluginState.queryText = null;
					newPluginState.mentionStart = null;
					newPluginState.mentionEnd = null;

				}

				return newPluginState;

			}

		},

		props: {

			handleTextInput(view, from, to, text) {

				const { isMentionActive, mentionStart, mentionEnd } = this.getState(view.state);
				const { tr, schema } = view.state;

				// Deactivate if user types non-word character in mention range
				if (isMentionActive && !text.match(/[\w-.]/)) {

					// Mark the tracked mention range so that we won't match it again
					tr.addMark(mentionStart, mentionEnd, schema.mark(SELECTION_KEY));
					tr.setMeta('deactivateMention', true);

					view.dispatch(tr);

				}

			},

			handleClick(view, pos) {

				const { isMentionActive, mentionStart, mentionEnd } = this.getState(view.state);
				const { tr, schema } = view.state;

				// Deactivate if user clicks anywhere else besides the end of
				// the mention range
				if (isMentionActive && pos !== mentionEnd) {

					// Mark the tracked mention range so that we won't match it again
					tr.addMark(mentionStart, mentionEnd, schema.mark(SELECTION_KEY));
					tr.setMeta('deactivateMention', true);

					view.dispatch(tr);

				}

			},

			handleKeyDown(view, event) {

				const { isMentionActive, selectRef } = this.getState(view.state);
				const { tr } = view.state;

				// Don't override key down behavior if mention state is not active
				if (!isMentionActive) {

					return false;

				}

				switch (event.key) {

				case 'Esc': // IE/Edge value
				case 'Escape':

					tr.setMeta('deactivateMention', true);
					view.dispatch(tr);
					break;

				case 'Left': // IE/Edge value
				case 'ArrowLeft':
				case 'Right': // IE/Edge value
				case 'ArrowRight':

					// Prevent default
					return true;

				case 'Enter':
				case 'Tab':
				case 'Down': // IE/Edge value
				case 'ArrowDown':
				case 'Up': // IE/Edge value
				case 'ArrowUp':

					if (selectRef) {

						// Trigger keydown event on react-select component
						selectRef.onKeyDown(event);

						// Prevent default
						return true;

					}

					break;

				default:

					return false;

				}

				return false;

			},

			decorations(state) {

				const { isMentionActive, mentionStart, mentionEnd } = this.getState(state);

				if (!isMentionActive) {

					return null;

				}

				// Wrap the current mention text in a span so we that we are
				// able to select it as a dom node and attach the dropdown popover
				return DecorationSet.create(state.doc, [
					Decoration.inline(mentionStart, mentionEnd, {
						nodeName: 'span',
					})
				]);

			},

		},

	});

export default mentionTriggerPlugin;
